import { AnalyticsMenuAction } from 'lib-analytics-ui';
import { DashboardType } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { pubSub } from 'utils/pubSub';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';

type UseDashboardLastRefreshOptions = {
  dashboardType: DashboardType;
};

type UseDashboardLastRefreshResult = {
  refreshAction: AnalyticsMenuAction;
};

/**
 * A hook that keeps track of the last time a real-time dashboard was refreshed,
 * either manually or through WebSocket updates, and provides a refresh action
 * that can be used to manually refresh the dashboard.
 */
export const useDashboardLastRefresh = ({
  dashboardType,
}: UseDashboardLastRefreshOptions): UseDashboardLastRefreshResult => {
  const [lastRefreshTimeStamp, setLastRefreshTimeStamp] = useState<number>(
    Date.now(),
  );
  const lastRefreshTimeAgo = useShortHumanReadableTimer(lastRefreshTimeStamp);

  useEffectOnce(() => {
    if (dashboardType === 'favorite') return;

    const unsubscribe = pubSub.subscribe(
      `real_time_dashboard:${dashboardType}_metrics_changed`,
      () => setLastRefreshTimeStamp(Date.now()),
    );

    return () => unsubscribe();
  });

  const handleManualRefresh = useCallback(() => {
    pubSub.publish('real_time_dashboard:refetch');
    setLastRefreshTimeStamp(Date.now());

    ampli.dashboardRefreshDataClicked();
  }, []);

  return useMemo(
    (): UseDashboardLastRefreshResult => ({
      refreshAction: {
        type: 'button',
        icon: 'redo',
        label: 'Refresh data',
        description: `Last refreshed ${lastRefreshTimeAgo}`,
        disabled: false,
        onClick: handleManualRefresh,
      },
    }),
    [lastRefreshTimeAgo, handleManualRefresh],
  );
};
